import { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ICONS_ALIASES, ICONS_PALETTE } from '_constants/icons';
import { LLCIncorporation_FAQ } from '_constants/faq';
import { ROUTES } from '_constants/routes';
import { PROCESSING_CODES } from '_constants/processing';
import { PRODUCT_CODES } from '_constants/products';
import { createQueryString } from '_helpers/urls';

import { refreshActiveProductCode } from '_redux/products/slice';
import { selectFormValues as selectFilingFormValues } from '_redux/order/selector';
import { selectPriceMapByProcessingCode } from '_redux/variants/selector';

import { Divider } from '_components/atomic/layouts';
import Circle from '_components/atomic/Circle';
import Dots from '_components/atomic/Dots';
import HowSection from '_components/composed/sections/HowSection';
import FancyList from '_components/atomic/FancyList';
import BottomBannerLLCSection from '_components/composed/sections/BottomBannerLLCSection';
import BottomNavigationSection from '_components/composed/sections/BottomNavigationSection';
import FAQSection from '_components/composed/sections/FAQSection';
import ProcessSection from '_components/composed/sections/ProcessSection';
import LLCProductBannerSection from '_components/composed/sections/LLCProductBannerSection';
import TestimonialsSection from '_components/composed/sections/TestimonialsSection';
import BaseInfoSection from '_components/composed/sections/BaseInfoSection';
import ComparingLLCSection from '_components/composed/sections/ComparingLLCSection';
import TrustPilotBannerSection from '_components/composed/sections/TrustPilotBannerSection';
import RegisterYourLLCFloat from '_components/composed/sections/RegisterYourLLCFloat';

const { colors, spaces } = theme;

const StyledLLCIncorporationPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const LLCIncorporationPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const filingFormValues = useSelector(selectFilingFormValues);

  const priceMapByProcessingCode = useSelector(
    selectPriceMapByProcessingCode(PROCESSING_CODES.gold)
  );

  const handleStartRegisteringLLC = useCallback(() => {
    router.push(
      `${ROUTES.llcIncorporationFiling}${createQueryString({
        step: filingFormValues?.step || 0,
      })}`,
      undefined,
      { shallow: true }
    );
  }, [router, filingFormValues]);

  useEffect(() => {
    dispatch(refreshActiveProductCode(PRODUCT_CODES.incLLC));
  }, [dispatch]);

  return (
    <StyledLLCIncorporationPage>
      <LLCProductBannerSection
        price={priceMapByProcessingCode[PRODUCT_CODES.incLLC]}
        onConfirm={handleStartRegisteringLLC}
      />

      <RegisterYourLLCFloat />
      <TrustPilotBannerSection />

      <Divider size={spaces.large12X} desktop />
      <Divider size={spaces.large} mobile />

      <BaseInfoSection
        imgSrc={`${config.basePath}/images/filing-llc-is-challenging-and-expensive.svg`}
        imgAlt="Incorpified Offered Services"
        subtitle=""
        title="Let us do the complicated work for you, for free"
        description={
          <>
            There&apos;s a lot that goes into filing an LLC. With various state
            requirements, multiple steps and a lot of important documentation,
            even the smallest mistake can end up costing you. That’s where we
            come in. We will handle the process from start to finish, all for as
            little as <b>$0</b>.
          </>
        }
      />

      <Divider size={spaces.large8X} desktop />

      <BaseInfoSection
        reverse
        imgSrc={`${config.basePath}/images/incorpify-is-easy-and-affordable.svg`}
        imgAlt="Thumbs up happy customers"
        subtitle=""
        title="Incorpified is easy and affordable"
        description={
          <>
            Did You Know? <br /> The popularity of LLCs has been rapidly growing
            in the United States, with over 2.5 million of them operating as of
            2021. These versatile business entities have become increasingly
            sought after due to their flexibility, limited liability protection,
            and tax advantages. As a result, LLCs have been growing at a faster
            rate than other business structures, demonstrating their appeal to
            entrepreneurs and small business owners nationwide.
          </>
        }
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <ComparingLLCSection />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <HowSection
        title="Advantages of Forming an LLC"
        rightPart={
          <FancyList
            items={[
              {
                title: 'Protect yourself and your assets',
                description:
                  "Forming an LLC offers you the added benefit of personal liability protection, meaning your personal finances are separated from the business. So, you can sleep better at night knowing you're protected.",
                icon: ICONS_ALIASES.shield,
                iconPalette: ICONS_PALETTE.indigo[ICONS_ALIASES.shield],
                color: colors.indigo[25],
                textColor: colors.indigo[500],
              },
              {
                title: 'Save on taxes',
                description:
                  'The LLC structure is considered "pass-through" for tax purposes, which means you\'ll pay less taxes overall. Who doesn\'t love saving money?',
                icon: ICONS_ALIASES.taxes2,
                iconPalette: ICONS_PALETTE.green[ICONS_ALIASES.taxes2],
                color: colors.green[50],
                textColor: colors.green[500],
              },
              {
                title: 'Tailor it to your needs',
                description:
                  'The LLC structure allows for more flexibility in terms of management and structure, giving you the freedom to create an internal operating agreement that works best for you and your business.',
                icon: ICONS_ALIASES.poverty,
                iconPalette: ICONS_PALETTE.blue[ICONS_ALIASES.poverty],
                color: colors.blue[25],
                textColor: colors.blue[500],
              },
              {
                title: 'Easy and affordable',
                description:
                  'Setting up and maintaining an LLC is generally easier and less expensive compared to other business structures.',
                icon: ICONS_ALIASES.piggyBank,
                iconPalette: ICONS_PALETTE.orange[ICONS_ALIASES.piggyBank],
                color: colors.orange[50],
                textColor: colors.orange[500],
              },
              {
                title: 'Boost your credibility',
                description:
                  'Forming an LLC can give your business more credibility in the eyes of customers and partners, as it shows that you take your business seriously and are willing to invest in its success.',
                icon: ICONS_ALIASES.rocket,
                iconPalette: ICONS_PALETTE.indigo[ICONS_ALIASES.rocket],
                color: colors.indigo[25],
                textColor: colors.indigo[500],
              },
            ]}
          />
        }
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <ProcessSection
        title="Filing Online with Incorpified"
        activeStepIdx={0}
        steps={[
          {
            title: 'STEP 01',
            subtitle: 'Choose a unique and compliant name for your LLC',
            description: (
              <span>
                Our team will help you pick the perfect name that aligns with
                your vision and complies with state guidelines.
              </span>
            ),
            imageSrc: `${config.basePath}/images/telecommuting.svg`,
            imageAlt: 'Cartoon working at laptop',
            reverse: false,
          },
          {
            title: 'STEP 02',
            subtitle: 'Give us some information about your business',
            description: (
              <span>
                Our simple questionnaire will help you provide us with the
                necessary information about your business and its owners, so we
                can get started on the incorporation process.
              </span>
            ),
            imageSrc: `${config.basePath}/images/forms.svg`,
            imageAlt: 'Animated LLC incorporation form',
            reverse: true,
          },
          {
            title: 'STEP 03',
            subtitle: 'Get Incorporated',
            description: (
              <span>
                Leave the paperwork to us, we will file all the necessary
                documents to officially incorporate your LLC with the
                state&#39;s business division. We&#39;ll make sure that
                everything is done correctly and efficiently, so you can focus
                on starting to grow your business.
              </span>
            ),
            imageSrc: `${config.basePath}/images/puzzle.svg`,
            imageAlt: 'Putting Puzzle Pieces together',
            reverse: false,
          },
        ]}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <TestimonialsSection
        title="Testimonials"
        testimonials={[
          {
            description:
              'Incorpified was the perfect choice for me when it came to incorporating my LLC. Their team made the process seamless and easy to understand. From choosing a unique business name to filing all the paperwork, they took care of everything with precision and expertise. I highly recommend Incorpified to anyone looking to incorporate their business and take their company to the next level.',
            author: 'David Thompson,',
            title: 'Vice President at Innovative Industries',
          },
          {
            description:
              'Incorpified made incorporating our golf simulator bar startup, an effortless experience. Their team guided us through the process and handled all the paperwork with precision and efficiency. Thanks to Incorpified, we were able to focus on growing our business. We highly recommend Incorpified to any new businesses looking to incorporate their LLC with ease and confidence.',
            author: 'Tom Mitchell,',
            title: 'CEO',
          },
          {
            description:
              "I recently used Incorpified to incorporate my LLC and I couldn't be happier with the results. The process was quick, easy and stress-free thanks to the guidance and expertise of the Incorpified team. From helping me choose the perfect business name to filing all the paperwork, they took care of everything with professionalism and efficiency. I highly recommend Incorpified to anyone looking to incorporate their LLC - with them, you'll be in good hands.\"",
            author: 'John Morrow,',
            title: 'Owner of new start up',
          },
        ]}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomBannerLLCSection
        price={priceMapByProcessingCode[PRODUCT_CODES.incLLC]}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <FAQSection items={LLCIncorporation_FAQ} />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomNavigationSection
        previousService={{
          title: 'Registered Agent',
          link: ROUTES.registeredAgent,
        }}
        nextService={{
          title: 'Bookkeeping',
          link: ROUTES.bookkeeping,
        }}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <Dots
        color={theme.colors.indigo[500]}
        right={210}
        top={`calc(24% + 10px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={610}
        right={-85}
        top={`calc(22% + 40px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        right={335}
        top={`calc(40% + 36px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        right={-123}
        top={`calc(42% + 0px)`}
      />

      <Dots
        color={theme.colors.green[500]}
        left={228}
        top={`calc(40% + 50px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.green[400],
          to: theme.colors.green[50],
        }}
        size={686}
        rotate={-90}
        left={-76}
        top={`calc(39% + 10px)`}
      />

      <Dots
        color={theme.colors.green[500]}
        right={280}
        top={`calc(84% + 70px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.green[400],
          to: theme.colors.green[50],
        }}
        size={686}
        right={-172}
        top={`calc(82% + 0px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        left={310}
        top={`calc(88% + 30px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={90}
        left={-133}
        top={`calc(89% - 16px)`}
      />
    </StyledLLCIncorporationPage>
  );
};

export default LLCIncorporationPage;
